/* @flow */
import CurrentUser from 'auth/CurrentUser';
import Electron from 'lib/Electron';
import GroupStore from 'groups/stores/GroupStore';
import LocalUserSettings from 'lib/LocalUserSettings';
import Store from 'lib/Store';
import WebNotification from 'notifications/WebNotification';
import { ChatMessage, chatMessageFactory } from 'entities/ChatMessage';
import { PushEvents } from 'pusher/PusherConstants';
import type { Options } from 'notifications/WebNotification';

// https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API/Using_the_Notifications_API#Getting_permission
type NotificationPermission = 'default' | 'granted' | 'denied';

// Note: Flux stores are now a DEPRECATED pattern. See docs/GraphQL.md
class WebNotificationStore extends Store {
  getInitialState() {
    return {
      seenChatMessageIds: [],
      notifications: [],
    };
  }

  getBrowserPermission(): NotificationPermission {
    if (!this.areNotificationsCompatible()) {
      return 'default';
    }
    return window.Notification.permission;
  }

  areNotificationsUserEnabled(): boolean {
    return (
      this.areNotificationsCompatible() &&
      LocalUserSettings.getUserProperties().settings.displayWebNotifications
    );
  }

  areNotificationsCompatible(): boolean {
    return window.Notification && LocalUserSettings.isEnabled();
  }

  areNotificationsDispatchable(): boolean {
    return (
      this.areNotificationsCompatible() &&
      LocalUserSettings.getUserProperties().settings.displayWebNotifications &&
      window.Notification.permission === 'granted'
    );
  }

  getPendingNotifications() {
    return this.state.notifications.filter(n => !n.isDismissed() && !n.isDispatched());
  }

  addNotification(options: Options) {
    if (this.areNotificationsDispatchable()) {
      this.state.notifications.push(new WebNotification(options));
    }
  }

  getOptionsForChangesetChatMessage(action: Object): Options[] {
    const seenChatMessageIds = this.state.seenChatMessageIds;
    const messages = action.changes
      .map(change => chatMessageFactory(change.content))
      .filter(message => {
        const show =
          message instanceof ChatMessage &&
          !seenChatMessageIds.includes(message.getUuid()) &&
          message.getSender().getUuid() !== CurrentUser.getUserUuid();

        if (show) {
          seenChatMessageIds.push(message.getUuid());
          return true;
        }
        return false;
      });

    const notifications = [];

    messages.forEach(message => {
      const body = message.getBody() ? message.getBody() : 'File uploaded';
      const url = `/classes/r/messages/${message.getChatId()}`;

      notifications.push({
        title: `New message from ${message.getSender().getName()}`,
        body,
        url,
        tracking: {
          ui_context: 'received_chat',
          foreign_table: 'chats',
          foreign_id: message.getChatId(),
        },
      });
    });

    return notifications;
  }

  getOptionsForClassMembership(action: Object): Options[] {
    const { model, type } = action;
    const group = GroupStore.getGroup(model.getGroupId());

    if (!group) {
      return [];
    }

    // Copy the backend logic: Don't show new class membership notifications
    // for staff classes.
    //
    // TODO: Receive desktop notifications from r101-api via Pusher so this
    // logic can be moved to r101-api where it can be shared with the mobile
    // apps.
    //
    // See https://app.asana.com/0/42793365058492/108770129145700.
    if (group.isStaffClass()) {
      return [];
    }

    if (
      type === 'update' &&
      model.getRole() === 'owner' &&
      model.getUserId() === CurrentUser.getUserId()
    ) {
      return [
        {
          title: 'Remind',
          body: `You’ve been added as a class owner to ${group.getName()}.`,
          url: `/classes/${group.getCodeOrError()}`,
          tracking: {
            ui_context: 'group_owner_promotion',
            foreign_table: 'groups',
            foreign_id: group.id,
          },
        },
      ];
    }
    return [];
  }
  getUrlForNotification(data: ?Object) {
    return data && data.stream_uuid ? `/classes/r/messages/${data.stream_uuid}` : null;
  }
  getOptionsForChangesetDesktopNotification(action: Object): Options[] {
    return action.changes.map(change => {
      return {
        title: 'Remind',
        body: change.content.body,
        url: this.getUrlForNotification(change.content.extra_payload),
      };
    });
  }
  getOptionsForTestMessage(): Options[] {
    return [
      {
        title: 'A Notification from Remind',
        body: 'Education is not the filling of a pail, but the lighting of a fire.',
      },
    ];
  }
  handlePushEvent(action: Object) {
    const {
      type,
    } = action; /* Skip notifications when page is focused, except for TEST_MESSAGE
     * We use this instead of `document.hidden` which would still be true if
     * app is backgrounded but the browser tab is foremost.
     */
    if (window.document.hasFocus() && type !== PushEvents.TEST_MESSAGE) {
      return;
    }
    const OPTIONS_FACTORIES = {
      [PushEvents.TEST_MESSAGE]: this.getOptionsForTestMessage,
      [PushEvents.CLASS_MEMBERSHIP]: this.getOptionsForClassMembership,
      [PushEvents.CHANGESET_CREATE_CHAT_MESSAGE]: this.getOptionsForChangesetChatMessage.bind(this),
      [PushEvents.CHANGESET_CREATE_DESKTOP_NOTIFICATION]: this.getOptionsForChangesetDesktopNotification.bind(
        this
      ),
    };
    const factory = OPTIONS_FACTORIES[type];
    const optionsCollection = factory(
      action
    ); /*
    * This resolves to the Remind icon shown when a notification is dispatched.
    * options.icon is not assigned on the desktop app as Electron handles the icon
    * for its local notifications
    */
    optionsCollection.forEach(options => {
      options.icon = !Electron.isAvailable() && '/assets/images/remind-square-logo-128.png';
      this.addNotification(options);
      this.emitChange();
    });
  }
  handleDispatch(action: Object): void {
    switch (action.type) {
      case PushEvents.TEST_MESSAGE:
      case PushEvents.CLASS_MEMBERSHIP:
      case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE:
      case PushEvents.CHANGESET_CREATE_DESKTOP_NOTIFICATION:
        this.handlePushEvent(action);
        break;
      default:
        break;
    }
  }
}
export { WebNotificationStore };
export default new WebNotificationStore();
